import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Spinner } from "../../components/Brand";
import LineItemsEditor from "./LineItemsEditor";

/**
 * Reusable admin list-and-editor for both Quotations and Tax Invoices.
 * `kind` = "quotation" | "invoice"
 *
 * Backend routes are inferred:
 *   /quotations  and /tax-invoices  (list, GET-by-id, POST, PATCH, DELETE, {id}/pdf)
 * Status-transition endpoints differ per kind and are declared in CONFIG.
 */

const CONFIG = {
    quotation: {
        label: "Quotations",
        subtitle: "Send quotes → track acceptance → convert to invoice",
        base: "/quotations",
        pdfName: (d) => `${d.number}.pdf`,
        statuses: ["draft", "sent", "accepted", "rejected", "expired", "converted"],
        statusColors: {
            draft: "bg-white/10 text-white/60 border-white/20",
            sent: "bg-blue-500/20 text-blue-300 border-blue-500/40",
            accepted: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
            rejected: "bg-rose-500/20 text-rose-300 border-rose-500/40",
            expired: "bg-amber-500/20 text-amber-300 border-amber-500/40",
            converted: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40",
        },
        actions: [
            { key: "send", label: "Mark Sent", from: ["draft"] },
            { key: "accept", label: "Mark Accepted", from: ["draft", "sent"] },
            { key: "reject", label: "Mark Rejected", from: ["draft", "sent"], needsReason: true },
            { key: "convert", label: "Convert → Invoice", from: ["draft", "sent", "accepted"], accent: true },
        ],
        numberPrefix: "QUO",
    },
    invoice: {
        label: "Tax Invoices",
        subtitle: "UAE 5% VAT · issue → mark paid → void",
        base: "/tax-invoices",
        pdfName: (d) => `${d.number}.pdf`,
        statuses: ["draft", "issued", "paid", "void"],
        statusColors: {
            draft: "bg-white/10 text-white/60 border-white/20",
            issued: "bg-blue-500/20 text-blue-300 border-blue-500/40",
            paid: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
            void: "bg-rose-500/20 text-rose-300 border-rose-500/40",
        },
        actions: [
            { key: "issue", label: "Issue Invoice", from: ["draft"] },
            { key: "mark-paid", label: "Mark Paid", from: ["issued"] },
            { key: "void", label: "Void", from: ["draft", "issued"], needsReason: true, danger: true },
        ],
        numberPrefix: "INV",
    },
};

const EMPTY_FORM = {
    customer_name: "", customer_email: "", customer_phone: "", customer_company: "",
    customer_address: "", issue_date: "", valid_until: "", due_date: "",
    currency: "AED", vat_rate: 5, items: [], notes: "", terms: "",
};

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}
function money(v, cur = "AED") { return `${cur} ${(+v || 0).toFixed(2)}`; }

export default function BillingPage({ kind }) {
    const cfg = CONFIG[kind];
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);
    const [reasonPrompt, setReasonPrompt] = useState(null); // { doc, action }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: "50" });
            if (q) params.set("q", q);
            if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
            const { data } = await api.get(`${cfg.base}?${params}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
            setCounters(data.counters || {});
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter, cfg.base]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setForm({ ...EMPTY_FORM, items: [{ part_id: "", description: "", sku: "", unit: "pc", qty: 1, unit_price: 0, discount_pct: 0 }] });
        setEditingId(null);
        setShowForm(true);
    };

    const openEdit = (doc) => {
        setForm({
            ...EMPTY_FORM,
            ...doc,
            issue_date: (doc.issue_date || "").slice(0, 10),
            valid_until: (doc.valid_until || "").slice(0, 10),
            due_date: (doc.due_date || "").slice(0, 10),
            items: (doc.items || []).map((it) => ({ ...it })),
        });
        setEditingId(doc.id);
        setShowForm(true);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
        if (form.items.length === 0) { toast.error("Add at least one line item"); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                items: form.items.map((it) => ({
                    part_id: it.part_id || null,
                    description: (it.description || "").trim(),
                    unit: it.unit || "pc",
                    qty: +it.qty,
                    unit_price: +it.unit_price,
                    discount_pct: +it.discount_pct || 0,
                    note: it.note || "",
                })),
                vat_rate: +form.vat_rate,
            };
            // Drop empty date strings so the backend picks defaults.
            for (const k of ["issue_date", "valid_until", "due_date"]) {
                if (!payload[k]) delete payload[k];
            }
            if (editingId) {
                await api.patch(`${cfg.base}/${editingId}`, payload);
                toast.success("Saved");
            } else {
                const { data } = await api.post(cfg.base, payload);
                toast.success(`${data.number} created`);
            }
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runAction = async (doc, action, reason = "") => {
        try {
            const body = action.needsReason ? { reason } : {};
            const { data } = await api.post(`${cfg.base}/${doc.id}/${action.key}`, body);
            if (action.key === "convert") {
                toast.success(`Invoice ${data.invoice?.number} created`);
            } else {
                toast.success(`Status: ${data.status || action.label}`);
            }
            setDetail(null); setReasonPrompt(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Action failed");
        }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Delete ${doc.number}? This cannot be undone.`)) return;
        try {
            await api.delete(`${cfg.base}/${doc.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    const downloadPdf = async (doc) => {
        try {
            const { data } = await api.get(`${cfg.base}/${doc.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = cfg.pdfName(doc); a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("PDF failed");
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Billing</div>
                    <h1 className="font-serif text-4xl text-white">{cfg.label}</h1>
                    <p className="text-xs text-white/40 mt-1">{cfg.subtitle} · {total.toLocaleString()} matching</p>
                </div>
                <button
                    data-testid={`${kind}-new-btn`}
                    onClick={openCreate}
                    className="lx-btn-gold px-5 py-2.5 text-sm"
                >
                    + New {kind === "quotation" ? "Quotation" : "Invoice"}
                </button>
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2">
                <Pill label={`All · ${counters.all || 0}`} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testid={`${kind}-filter-all`} />
                {cfg.statuses.map((s) => (
                    <Pill
                        key={s}
                        label={`${s} · ${counters[s] || 0}`}
                        active={statusFilter === s}
                        onClick={() => setStatusFilter(s)}
                        testid={`${kind}-filter-${s}`}
                    />
                ))}
            </div>

            {/* Search */}
            <input
                data-testid={`${kind}-search`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search number, customer, email…`}
                className="w-full bg-[#0F0614] border border-[#2B1438] px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
            />

            {/* Table */}
            <div className="lx-card-dark overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-white/40 uppercase tracking-wider text-[10px] border-b border-[#2B1438]">
                                <th className="text-left py-3 px-4">Number</th>
                                <th className="text-left py-3 px-4">Customer</th>
                                <th className="text-left py-3 px-4">{kind === "quotation" ? "Valid Until" : "Due Date"}</th>
                                <th className="text-right py-3 px-4">Total</th>
                                <th className="text-center py-3 px-4">Status</th>
                                <th className="text-right py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (<tr><td colSpan={6} className="text-center py-12"><Spinner className="mx-auto" /></td></tr>)}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-white/40 py-12">No {cfg.label.toLowerCase()} yet.</td></tr>
                            )}
                            {items.map((d) => (
                                <tr key={d.id} data-testid={`${kind}-row-${d.id}`} className="border-t border-[#2B1438]/60 hover:bg-[#180A21]/60">
                                    <td className="py-3 px-4 font-mono text-[#D4AF37]">{d.number}</td>
                                    <td className="py-3 px-4 text-white">
                                        {d.customer_name}
                                        {d.customer_email && <div className="text-[10px] text-white/40">{d.customer_email}</div>}
                                    </td>
                                    <td className="py-3 px-4 text-white/70">{fmtDate(kind === "quotation" ? d.valid_until : d.due_date)}</td>
                                    <td className="py-3 px-4 text-white text-right tabular-nums">{money(d.total_amount, d.currency)}</td>
                                    <td className="py-3 px-4 text-center">
                                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${cfg.statusColors[d.status] || "bg-white/10 text-white/40 border-white/20"}`}>{d.status}</span>
                                    </td>
                                    <td className="py-3 px-4 text-right space-x-3">
                                        <button data-testid={`${kind}-view-${d.id}`} onClick={() => setDetail(d)} className="text-[#D4AF37] hover:underline text-xs">View</button>
                                        <button data-testid={`${kind}-pdf-${d.id}`} onClick={() => downloadPdf(d)} className="text-white/70 hover:text-white text-xs">PDF</button>
                                        {(d.status === "draft") && (
                                            <button data-testid={`${kind}-edit-${d.id}`} onClick={() => openEdit(d)} className="text-white/70 hover:text-white text-xs">Edit</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail modal */}
            {detail && (
                <DetailModal
                    doc={detail}
                    cfg={cfg}
                    kind={kind}
                    onClose={() => setDetail(null)}
                    onAction={(action) => {
                        if (action.needsReason) setReasonPrompt({ doc: detail, action });
                        else runAction(detail, action);
                    }}
                    onEdit={() => { openEdit(detail); setDetail(null); }}
                    onDelete={() => { remove(detail); setDetail(null); }}
                    onPdf={() => downloadPdf(detail)}
                />
            )}

            {/* Reason prompt */}
            {reasonPrompt && (
                <ReasonModal
                    action={reasonPrompt.action}
                    onCancel={() => setReasonPrompt(null)}
                    onConfirm={(reason) => runAction(reasonPrompt.doc, reasonPrompt.action, reason)}
                />
            )}

            {/* Create/edit modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#180A21] border border-[#2B1438] w-full max-w-3xl my-8 p-6 space-y-5" data-testid={`${kind}-form-modal`}>
                        <div className="flex justify-between items-center">
                            <h3 className="font-serif text-2xl text-white">{editingId ? "Edit" : "New"} {kind === "quotation" ? "Quotation" : "Tax Invoice"}</h3>
                            <button type="button" onClick={() => setShowForm(false)} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Customer Name *">
                                <input required data-testid={`${kind}-cust-name`} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="lx-in-form" />
                            </Field>
                            <Field label="Customer Email">
                                <input type="email" data-testid={`${kind}-cust-email`} value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="lx-in-form" />
                            </Field>
                            <Field label="Mobile">
                                <input data-testid={`${kind}-cust-phone`} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="lx-in-form" />
                            </Field>
                            <Field label="Company">
                                <input value={form.customer_company} onChange={(e) => setForm({ ...form, customer_company: e.target.value })} className="lx-in-form" />
                            </Field>
                        </div>
                        <Field label="Address">
                            <textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="lx-in-form resize-none" />
                        </Field>

                        <div className="grid grid-cols-3 gap-4">
                            <Field label="Issue Date" hint="Any date, past or future. Leave blank for today.">
                                <div className="flex gap-1">
                                    <input
                                        type="date"
                                        data-testid={`${kind}-issue-date`}
                                        value={form.issue_date}
                                        onChange={(e) => {
                                            const iso = e.target.value;
                                            setForm((f) => {
                                                const next = { ...f, issue_date: iso };
                                                // Auto-suggest the trailing date only if the user hasn't set it
                                                // themselves — never overwrite a manual pick.
                                                const trailKey = kind === "quotation" ? "valid_until" : "due_date";
                                                if (iso && !f[trailKey]) {
                                                    const days = kind === "quotation" ? 15 : 15;
                                                    const d = new Date(iso);
                                                    d.setDate(d.getDate() + days);
                                                    next[trailKey] = d.toISOString().slice(0, 10);
                                                }
                                                return next;
                                            });
                                        }}
                                        className="lx-in-form flex-1"
                                    />
                                    <button
                                        type="button"
                                        data-testid={`${kind}-issue-today`}
                                        onClick={() => setForm((f) => ({ ...f, issue_date: new Date().toISOString().slice(0, 10) }))}
                                        title="Set to today"
                                        className="px-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/60 hover:text-[#D4AF37] hover:border-[#D4AF37]/60"
                                    >
                                        Today
                                    </button>
                                </div>
                            </Field>
                            <Field label={kind === "quotation" ? "Valid Until" : "Due Date"} hint="Auto-fills 15 days after issue">
                                <input
                                    type="date"
                                    data-testid={`${kind}-trail-date`}
                                    value={kind === "quotation" ? form.valid_until : form.due_date}
                                    onChange={(e) => setForm({ ...form, [kind === "quotation" ? "valid_until" : "due_date"]: e.target.value })}
                                    className="lx-in-form"
                                />
                            </Field>
                            <Field label="VAT %">
                                <input type="number" min="0" max="100" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} className="lx-in-form" />
                            </Field>
                        </div>

                        <LineItemsEditor
                            items={form.items}
                            onChange={(items) => setForm({ ...form, items })}
                            currency={form.currency}
                            vatRate={+form.vat_rate || 0}
                        />

                        <Field label="Notes (optional)">
                            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="lx-in-form resize-none" />
                        </Field>

                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70">Cancel</button>
                            <button type="submit" data-testid={`${kind}-save-btn`} disabled={saving} className="lx-btn-gold px-6 py-2 text-sm disabled:opacity-60">
                                {saving ? <Spinner /> : editingId ? "Save Changes" : `Create ${kind === "quotation" ? "Quote" : "Invoice"}`}
                            </button>
                        </div>
                        <style>{`.lx-in-form{width:100%;padding:0.6rem 0.75rem;background:#0F0614;border:1px solid #2B1438;color:white;font-size:.875rem;outline:none}.lx-in-form:focus{border-color:#D4AF37}`}</style>
                    </form>
                </div>
            )}
        </div>
    );
}

function Pill({ label, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 text-xs uppercase tracking-wider border transition ${active ? "bg-[#D4AF37] text-[#180A21] border-[#D4AF37]" : "border-[#2B1438] text-white/60 hover:text-white hover:border-[#D4AF37]/50"}`}
        >
            {label}
        </button>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="lx-label text-white/50 mb-1.5">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
        </label>
    );
}

function DetailModal({ doc, cfg, kind, onClose, onAction, onEdit, onDelete, onPdf }) {
    const c = cfg.statusColors[doc.status] || "";
    const applicable = cfg.actions.filter((a) => a.from.includes(doc.status));
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-[#180A21] border border-[#2B1438] w-full max-w-3xl my-8" data-testid={`${kind}-detail-modal`}>
                <div className="p-6 border-b border-[#2B1438] flex justify-between items-start">
                    <div>
                        <div className="lx-label text-[#D4AF37] mb-2">{cfg.label.slice(0, -1)}</div>
                        <div className="font-mono text-xl text-white">{doc.number}</div>
                        <div className="text-xs text-white/40 mt-1">Issued {fmtDate(doc.issue_date)}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${c}`}>{doc.status}</span>
                        <button onClick={onClose} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="lx-label text-white/40 mb-1">Bill To</div>
                            <div className="text-white">{doc.customer_name}</div>
                            {doc.customer_company && <div className="text-white/60 text-xs">{doc.customer_company}</div>}
                            {doc.customer_email && <div className="text-white/60 text-xs">{doc.customer_email}</div>}
                            {doc.customer_phone && <div className="text-white/60 text-xs">{doc.customer_phone}</div>}
                            {doc.customer_address && <div className="text-white/60 text-xs whitespace-pre-wrap">{doc.customer_address}</div>}
                        </div>
                        <div className="text-right space-y-1">
                            <Row label="Subtotal" value={money(doc.subtotal, doc.currency)} />
                            <Row label="Discount" value={`- ${money(doc.discount_total, doc.currency)}`} />
                            <Row label={`VAT (${doc.vat_rate}%)`} value={money(doc.vat_amount, doc.currency)} />
                            <Row label="Total" value={money(doc.total_amount, doc.currency)} highlight />
                            {kind === "quotation" && doc.valid_until && <Row label="Valid Until" value={fmtDate(doc.valid_until)} />}
                            {kind === "invoice" && doc.due_date && <Row label="Due Date" value={fmtDate(doc.due_date)} />}
                            {doc.linked_quotation_number && <Row label="From Quote" value={doc.linked_quotation_number} />}
                            {doc.converted_to_invoice_number && <Row label="Invoice" value={doc.converted_to_invoice_number} />}
                        </div>
                    </div>

                    <LineItemsEditor items={doc.items || []} onChange={() => {}} currency={doc.currency} vatRate={doc.vat_rate} readonly />

                    {doc.notes && (
                        <div className="text-sm text-white/70 border-t border-[#2B1438] pt-3">
                            <div className="lx-label text-white/40 mb-1">Notes</div>
                            <div className="whitespace-pre-wrap">{doc.notes}</div>
                        </div>
                    )}
                </div>
                <div className="p-6 border-t border-[#2B1438] flex justify-between flex-wrap gap-3">
                    <div className="flex gap-2">
                        <button data-testid={`${kind}-detail-pdf`} onClick={onPdf} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#180A21]">Download PDF</button>
                        {doc.status === "draft" && (
                            <>
                                <button data-testid={`${kind}-detail-edit`} onClick={onEdit} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70 hover:text-white">Edit</button>
                                <button data-testid={`${kind}-detail-delete`} onClick={onDelete} className="px-4 py-2 text-xs uppercase tracking-wider border border-rose-400/40 text-rose-300 hover:bg-rose-500/20">Delete</button>
                            </>
                        )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {applicable.map((a) => (
                            <button
                                key={a.key}
                                data-testid={`${kind}-act-${a.key}`}
                                onClick={() => onAction(a)}
                                className={`px-4 py-2 text-xs uppercase tracking-wider border ${a.accent ? "bg-[#D4AF37] text-[#180A21] border-[#D4AF37]" : a.danger ? "border-rose-400/40 text-rose-300 hover:bg-rose-500/20" : "border-[#2B1438] text-white/70 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"}`}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ReasonModal({ action, onCancel, onConfirm }) {
    const [reason, setReason] = useState("");
    return (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
            <div className="bg-[#180A21] border border-[#2B1438] w-full max-w-md p-6 space-y-4" data-testid="reason-modal">
                <h3 className="font-serif text-xl text-white">{action.label}</h3>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason…" className="w-full p-2.5 bg-[#0F0614] border border-[#2B1438] text-white text-sm outline-none focus:border-[#D4AF37]" data-testid="reason-input" />
                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70">Cancel</button>
                    <button data-testid="reason-confirm" onClick={() => onConfirm(reason)} className="px-4 py-2 text-xs uppercase tracking-wider bg-[#D4AF37] text-[#180A21]">Confirm</button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, highlight }) {
    return (
        <div className="flex justify-between text-xs">
            <span className="text-white/50">{label}</span>
            <span className={`tabular-nums ${highlight ? "text-[#D4AF37] font-serif text-base" : "text-white"}`}>{value}</span>
        </div>
    );
}
